import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { AiChat } from "@/components/AiChat";
import { api } from "@/lib/api";
import { CaretLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

function SubstepText({ text }) {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return (
    <span>
      {parts.map((p, i) =>
        /^https?:\/\//.test(p) ? (
          <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--bk-accent)" }}>{p}</a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </span>
  );
}

export default function GuideView() {
  const { phase } = useParams();
  const { user } = useAuth();
  const isOwnerMode = (user?.mode || "bookkeeper") === "owner";
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    api.get(`/guides/${phase}`).then((r) => setGuide(r.data)).catch(() => setGuide(false));
  }, [phase]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-10 pb-32">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium mb-6 hover:underline" style={{ color: "var(--bk-text-2)" }}>
          <CaretLeft size={16} weight="bold" /> Back to dashboard
        </Link>
        {guide === null ? (
          <p style={{ color: "var(--bk-text-2)" }}>Loading…</p>
        ) : guide === false ? (
          <p style={{ color: "var(--bk-text-2)" }}>Guide not found.</p>
        ) : (
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tight" style={{ color: "var(--bk-text)" }}>{guide.title}</h1>
            <p className="text-base mt-2 mb-10" style={{ color: "var(--bk-text-2)" }}>{guide.subtitle}</p>
            {isOwnerMode && guide.sections.some((s) => s.steps.some((st) => st.mode === "bookkeeper")) && (
              <div className="rounded-xl border px-4 py-3 text-sm mb-8" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="owner-mode-note">
                Rowan handles all double-entry mechanics (T-accounts, debits & credits, trial balance) automatically. Switch to Bookkeeper mode in the header for the optional "learn bookkeeping basics" steps.
              </div>
            )}
            {guide.sections.map((section) => {
              const visibleSteps = section.steps.filter((st) => !(isOwnerMode && st.mode === "bookkeeper"));
              if (!visibleSteps.length) return null;
              return (
              <div key={section.title} className="mb-10">
                <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: "var(--bk-accent)" }}>{section.title}</h3>
                <div className="space-y-4">
                  {visibleSteps.map((step) => (
                    <div key={step.id} className="bk-card p-5" data-testid={`guide-step-${step.id}`}>
                      <h4 className="font-semibold text-base" style={{ color: "var(--bk-text)" }}>{step.title}</h4>
                      <p className="text-sm leading-relaxed mt-1.5" style={{ color: "var(--bk-text-2)" }}>{step.detail}</p>
                      {step.substeps && step.substeps.length > 0 && (
                        <ol className="space-y-2 mt-3">
                          {step.substeps.map((ss, i) => (
                            <li key={i} className="flex gap-3 text-sm leading-relaxed" style={{ color: "var(--bk-text)" }}>
                              <span className="font-mono text-xs mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{i + 1}</span>
                              <span><SubstepText text={ss} /></span>
                            </li>
                          ))}
                        </ol>
                      )}
                      {step.doc_checklist_by_entity && (
                        <div className="rounded-md p-3 mt-3" style={{ background: "var(--bk-success-bg)" }}>
                          <p className="text-xs uppercase tracking-[0.12em] font-semibold mb-1.5" style={{ color: "var(--bk-success)" }}>What to bring, by entity type</p>
                          {Object.entries(step.doc_checklist_by_entity).map(([ent, docs]) => (
                            <p key={ent} className="text-sm mb-1" style={{ color: "var(--bk-text)" }}>
                              <span className="font-semibold">{ent}: </span>{docs.join(" · ")}
                            </p>
                          ))}
                        </div>
                      )}
                      {step.tip && (
                        <div className="flex gap-2 text-sm leading-relaxed rounded-md p-3 mt-3" style={{ background: "#F6EEDF", color: "var(--bk-text)" }}>
                          <span><span className="font-semibold">Tip: </span>{step.tip}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>
      <AiChat />
    </div>
  );
}
